<template>
  <div class="premium-date-wrap">
    <label v-if="label" class="premium-label">{{ label }}</label>

    <v-menu
      ref="menu"
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      left
      min-width="auto"
      :nudge-bottom="4"
    >
      <!-- ✅ Activator -->
      <template #activator="{ on, attrs }">
        <div
          class="premium-date-trigger"
          :class="{
            'is-active': menu,
            'is-filled': hasValue,
            'has-error': !!error,
          }"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon size="18" class="icon-main">
            {{ type === "month" ? "mdi-calendar-month" : "mdi-calendar" }}
          </v-icon>

          <span class="date-text">{{ displayValue }}</span>

          <!-- Clear button -->
          <button
            v-if="hasValue && !menu"
            class="clear-btn"
            @click.stop="clearDate"
            type="button"
          >
            <v-icon size="14">mdi-close</v-icon>
          </button>

          <!-- Chevron -->
          <v-icon v-else size="16" class="icon-chevron">
            mdi-chevron-down
          </v-icon>
        </div>
      </template>

      <!-- ✅ Date Picker -->
      <v-date-picker
        v-model="internalValue"
        :type="type"
        :range="range"
        no-title
        scrollable
        :color="primaryColor"
        class="premium-picker"
      >
        <v-spacer></v-spacer>

        <v-btn text class="btn-premium-cancel" @click="cancel"> Cancel </v-btn>

        <v-btn
          depressed
          class="btn-premium-primary ml-2"
          :style="{ background: brandColor }"
          @click="save"
        >
          OK
        </v-btn>
      </v-date-picker>
    </v-menu>

    <!-- Error -->
    <div v-if="error" class="error-msg">
      {{ Array.isArray(error) ? error[0] : error }}
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "PremiumDatePicker",
  props: {
    value: { type: [String, Array], default: "" },
    type: { type: String, default: "date" }, // 'date' or 'month'
    range: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select Date" },
    format: { type: String, default: "" },
    label: { type: String, default: "" },
    error: { type: [String, Array], default: "" },
  },
  data() {
    return {
      menu: false,
      internalValue: this.value,
    };
  },
  watch: {
    value(val) {
      this.internalValue = val;
    },
  },
  computed: {
    ...mapState("theme", ["primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#0d9972";
    },
    hasValue() {
      if (this.range) return Array.isArray(this.value) && this.value.length > 0;
      return !!this.value;
    },
    displayValue() {
      if (!this.hasValue) return this.placeholder;

      const fmt =
        this.format || (this.type === "month" ? "MMMM YYYY" : "DD MMM, YYYY");

      if (this.range && Array.isArray(this.value)) {
        if (this.value.length === 0) return this.placeholder;
        if (this.value.length === 1) return moment(this.value[0]).format(fmt);
        // Sort dates for display
        const sorted = [...this.value].sort();
        // If both dates are the same, show only once
        if (sorted[0] === sorted[1]) return moment(sorted[0]).format(fmt);
        return `${moment(sorted[0]).format(fmt)} - ${moment(sorted[1]).format(fmt)}`;
      }

      return moment(this.value).format(fmt);
    },
  },
  methods: {
    save() {
      this.$emit("input", this.internalValue);
      this.$emit("change", this.internalValue);
      this.menu = false;
    },
    cancel() {
      this.internalValue = this.value; // Reset to original value
      this.menu = false;
    },
    clearDate() {
      this.internalValue = this.range ? [] : "";
      this.$emit("input", this.internalValue);
      this.$emit("change", this.internalValue);
    },
  },
};
</script>

<style scoped>
.premium-date-wrap {
  margin-bottom: 4px;
  width: 100%;
}

.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

.premium-date-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 14px;
  background: var(--surface2);
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1));
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-width: 180px;
  user-select: none;
  position: relative;
}
.premium-date-trigger:hover {
  border-color: var(--brand, #0d9972);
}

.premium-date-trigger.is-active {
  border-color: var(--brand, #0d9972);
  box-shadow: 0 0 0 3px rgba(13, 153, 114, 0.12);
}

.premium-date-trigger.has-error {
  border-color: #ef4444;
}

.premium-date-trigger.is-filled {
  color: var(--tx, #111827);
}

.icon-main {
  color: var(--tx3, #9ca3af) !important;
  flex-shrink: 0;
  transition: color 0.2s;
}

.premium-date-trigger.is-active .icon-main {
  color: var(--brand, #0d9972) !important;
}
.premium-date-trigger.is-filled .icon-main {
  color: var(--brand, #0d9972) !important;
}

.date-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-chevron {
  color: var(--tx4, #9ca3af) !important;
  flex-shrink: 0;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-left: auto;
}

.premium-date-trigger.is-active .icon-chevron {
  transform: rotate(180deg);
}

/* Clear button */
.clear-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: var(--tx3) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: auto;
}

.clear-btn:hover {
  background: var(--brand);
  color: #fff !important;
}

/* Dark mode */
.theme--dark .premium-date-trigger {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .premium-date-trigger:hover,
.theme--dark .premium-date-trigger.is-active {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--brand);
}

.theme--dark .premium-date-trigger.is-filled {
  color: #f1f5f9;
}

.error-msg {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
  padding-left: 4px;
}

/* Picker buttons */
/* .btn-premium-cancel {
  color: var(--tx2, #475569) !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
}

.btn-premium-cancel:hover {
  background: var(--surface2, #f8fafc) !important;
} */

.btn-premium-primary {
  color: #ffffff !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  min-width: 60px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.btn-premium-primary:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}
</style>
